<template>
  <div v-if="showModuloForm === false">
    <MarketingModules></MarketingModules>
  </div>
  <div class="page matriz-rfm-page" v-else-if="showModuloForm === true">
    <div v-if="carregando" class="rfm-loading">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <p class="nota-informativa">
        <v-icon size="14" color="primario">fas fa-info-circle</v-icon>
        <strong>Como o motor RFM funciona:</strong> aqui você define os
        intervalos das notas de 1 a 5 de cada dimensão. O LifeGrowth calcula o
        score de cada cliente (por exemplo <strong>555</strong>,
        <strong>543</strong> ou <strong>221</strong>) no processamento
        periódico, com base nas compras faturadas.
      </p>

      <MatrizRfmGeral
        :config="draft"
        :ultimaExecucao="ultimaExecucaoFormatada"
        @alterar="alterarGeral"
      />

      <MatrizRfmRecencia
        :faixas="faixasRecencia"
        :erros="errosRecencia"
        @alterar="alterarRecencia"
        @restaurar="restaurar('recencia')"
      />

      <MatrizRfmFrequencia
        :faixas="faixasFrequencia"
        :erros="errosFrequencia"
        @alterar="alterarFrequencia"
        @restaurar="restaurar('frequencia')"
      />

      <MatrizRfmMonetario
        :faixas="faixasMonetario"
        :erros="errosMonetario"
        @alterar="alterarMonetario"
        @restaurar="restaurar('monetario')"
      />

      <MatrizRfmSimulador
        :insumos="insumos"
        :resultado="simulacao"
        :carregando="simulando"
        :erro="erroSimulacao"
        :bloqueado="temErros"
        :segmentos="segmentosGuia"
        @alterar="alterarInsumo"
      />

      <div class="rfm-barra-acoes">
        <Panel lineColor="var(--primary-color)" id="barra-acoes-salvamento">
          <div class="rfm-barra-conteudo">
            <div class="rfm-status-alteracoes">
              <template v-if="temErros">
                <v-icon size="14" color="error"
                  >fas fa-exclamation-circle</v-icon
                >
                <span class="texto-erro">
                  Existem {{ erros.length }} inconsistência(s) nos intervalos.
                  Corrija-as para salvar.
                </span>
              </template>
              <template v-else-if="dirty">
                <v-icon size="14" color="warning">fas fa-info-circle</v-icon>
                <span class="texto-alerta">
                  Você possui alterações não salvas nos parâmetros da Matriz
                  RFM.
                </span>
              </template>
              <template v-else>
                <v-icon size="14" color="success">fas fa-check-circle</v-icon>
                <span>
                  {{
                    configPersistida
                      ? "Configurações salvas e válidas."
                      : "Matriz ainda não configurada. Os valores padrão estão carregados."
                  }}
                </span>
              </template>
            </div>

            <div class="rfm-barra-botoes">
              <FormButton
                type="perigo"
                text="Cancelar"
                id="btn-cancelar-configuracao"
                :disabled="!dirty || salvando"
                @click="cancelar"
              />
              <FormButton
                outlined
                type="primario"
                color="primario"
                text="Valores padrão"
                id="btn-restaurar-padroes"
                :disabled="salvando"
                @click="restaurarTudo"
              />
              <FormButton
                type="submissao"
                text="Salvar configuração"
                id="btn-salvar-configuracao"
                :disabled="temErros"
                :loading="salvando"
                @click="onSalvar"
              />
            </div>
          </div>
        </Panel>
      </div>
    </template>
  </div>
</template>

<script>
import MarketingModules from "@/components/MarketingModules";
import FormButton from "@/components/form/FormButton";
import Panel from "@/components/Panel";
import MatrizRfmGeral from "./MatrizRfmGeral";
import MatrizRfmRecencia from "./MatrizRfmRecencia";
import MatrizRfmFrequencia from "./MatrizRfmFrequencia";
import MatrizRfmMonetario from "./MatrizRfmMonetario";
import MatrizRfmSimulador from "./MatrizRfmSimulador";
import confirmAlert from "@/mixins/confirmAlert";
import { mapGetters } from "vuex";
import { ModulesEnum } from "@/store/actions/modulesEnum";
import {
  RFM_OBTER_CONFIG,
  RFM_SALVAR_CONFIG,
  RFM_SIMULAR,
  RFM_LISTAR_SEGMENTOS
} from "@/store/actions/rfm";
import {
  configPadrao,
  insumosPadrao,
  normalizarConfig,
  configParaPayload,
  faixasRecencia,
  faixasFrequencia,
  faixasMonetario,
  aplicarAteRecencia,
  aplicarAteFrequencia,
  aplicarAteMonetario,
  restaurarDimensao,
  validarConfig
} from "@/utils/rfm-faixas";
import { guiaSegmentos } from "@/utils/rfm-segmentos";
import * as _ from "lodash";

const DEBOUNCE_SIMULACAO_MS = 400;

export default {
  name: "MatrizRfm",
  components: {
    MarketingModules,
    FormButton,
    Panel,
    MatrizRfmGeral,
    MatrizRfmRecencia,
    MatrizRfmFrequencia,
    MatrizRfmMonetario,
    MatrizRfmSimulador
  },
  mixins: [confirmAlert],
  data: () => ({
    draft: configPadrao(),
    configSalva: configPadrao(),
    configPersistida: false,
    metadados: null,
    salvando: false,
    carregou: false,
    carregando: true,
    insumos: insumosPadrao(),
    simulacao: null,
    simulando: false,
    erroSimulacao: "",
    segmentosGuia: guiaSegmentos(),
    modulosForm: [ModulesEnum.LIFEGROWTH]
  }),
  computed: {
    ...mapGetters(["getFornecedorId", "moduloFornecedorAtivo"]),
    showModuloForm() {
      return this.moduloFornecedorAtivo(this.modulosForm);
    },
    faixasRecencia() {
      return faixasRecencia(this.draft);
    },
    faixasFrequencia() {
      return faixasFrequencia(this.draft);
    },
    faixasMonetario() {
      return faixasMonetario(this.draft);
    },
    erros() {
      return validarConfig(this.draft);
    },
    temErros() {
      return this.erros.length > 0;
    },
    errosRecencia() {
      return this.erros.filter(err => err.dimensao === "recencia");
    },
    errosFrequencia() {
      return this.erros.filter(err => err.dimensao === "frequencia");
    },
    errosMonetario() {
      return this.erros.filter(err => err.dimensao === "monetario");
    },
    dirty() {
      return !_.isEqual(
        configParaPayload(this.draft),
        configParaPayload(this.configSalva)
      );
    },
    ultimaExecucaoFormatada() {
      const fim = _.get(this.metadados, "fim_ultima_execucao", null);
      if (!fim) return "";
      const data = new Date(fim);
      if (isNaN(data.getTime())) return "";
      return `${data.toLocaleDateString("pt-BR")} ${data.toLocaleTimeString(
        "pt-BR",
        {
          hour: "2-digit",
          minute: "2-digit"
        }
      )}`;
    }
  },
  created() {
    this.simularComDebounce = _.debounce(this.simular, DEBOUNCE_SIMULACAO_MS);
    this.carregarSeModuloAtivo();
  },
  beforeDestroy() {
    if (this.simularComDebounce) {
      this.simularComDebounce.cancel();
    }
  },
  watch: {
    // `moduloFornecedorAtivo` é undefined enquanto o fornecedor não carrega:
    // só buscamos os dados quando o módulo LifeGrowth for confirmado.
    showModuloForm() {
      this.carregarSeModuloAtivo();
    },
    draft: {
      deep: true,
      handler() {
        this.agendarSimulacao();
      }
    },
    insumos: {
      deep: true,
      handler() {
        this.agendarSimulacao();
      }
    }
  },
  methods: {
    carregarSeModuloAtivo() {
      if (this.showModuloForm !== true || this.carregou) return;
      this.carregou = true;
      this.carregarConfig();
      this.carregarSegmentos();
    },
    /** GET 404 = fornecedor ainda não configurou a matriz: usa os padrões. */
    carregarConfig() {
      this.$store
        .dispatch(RFM_OBTER_CONFIG)
        .then(config => {
          if (config) {
            this.metadados = config;
            this.configSalva = normalizarConfig(config);
            this.configPersistida = true;
          } else {
            this.metadados = null;
            this.configSalva = configPadrao();
            this.configPersistida = false;
          }
          this.draft = { ...this.configSalva };
          this.carregando = false;
          this.agendarSimulacao();
        })
        .catch(() => {
          // erro já reportado pela action; a tela segue com os valores padrão
          this.carregando = false;
        });
    },
    carregarSegmentos() {
      this.$store
        .dispatch(RFM_LISTAR_SEGMENTOS)
        .then(nomes => {
          this.segmentosGuia = guiaSegmentos(nomes);
        })
        .catch(() => {
          this.segmentosGuia = guiaSegmentos();
        });
    },
    alterarGeral(alteracao) {
      this.draft = { ...this.draft, ...alteracao };
    },
    alterarRecencia({ score, valor }) {
      this.draft = aplicarAteRecencia(this.draft, score, valor);
    },
    alterarFrequencia({ score, valor }) {
      this.draft = aplicarAteFrequencia(this.draft, score, valor);
    },
    alterarMonetario({ score, valor }) {
      this.draft = aplicarAteMonetario(this.draft, score, valor);
    },
    restaurar(dimensao) {
      this.draft = restaurarDimensao(this.draft, dimensao);
    },
    /** Restaura faixas, configuração geral e também os insumos do simulador. */
    restaurarTudo() {
      this.draft = configPadrao();
      this.insumos = insumosPadrao();
    },
    cancelar() {
      this.draft = { ...this.configSalva };
    },
    alterarInsumo({ campo, valor }) {
      const numero = valor === "" || valor === null ? null : Number(valor);
      this.insumos = {
        ...this.insumos,
        [campo]: Number.isFinite(numero) ? numero : null
      };
    },
    agendarSimulacao() {
      if (this.simularComDebounce) {
        this.simularComDebounce();
      }
    },
    /**
     * Nota, score composto e segmento vêm exclusivamente da API. O rascunho da
     * tela vai em `config` para permitir simular faixas ainda não salvas.
     */
    simular() {
      if (this.temErros) {
        this.simulacao = null;
        this.erroSimulacao = "";
        this.simulando = false;
        return;
      }

      const dias = this.insumos.diasDesdeUltimoPedido;
      const pedidos = this.insumos.qtdPedidosJanela;
      const valor = this.insumos.somaTotalliquidoJanela;

      const insumosIncompletos =
        !Number.isFinite(pedidos) ||
        pedidos < 0 ||
        !Number.isFinite(valor) ||
        valor < 0 ||
        (dias !== null && (!Number.isFinite(dias) || dias < 0));

      if (insumosIncompletos) {
        this.simulacao = null;
        this.simulando = false;
        this.erroSimulacao =
          "Informe dias, pedidos e valor faturado (números maiores ou iguais a zero) para simular.";
        return;
      }

      this.simulando = true;
      this.erroSimulacao = "";

      this.$store
        .dispatch(RFM_SIMULAR, {
          diasDesdeUltimoPedido: dias,
          qtdPedidosJanela: pedidos,
          somaTotalliquidoJanela: valor,
          config: configParaPayload(this.draft)
        })
        .then(resultado => {
          this.simulacao = resultado;
          this.simulando = false;
        })
        .catch(err => {
          this.simulacao = null;
          this.simulando = false;
          this.erroSimulacao =
            _.get(err, "response.data.error") ||
            "Não foi possível simular a alocação. Tente novamente.";
        });
    },
    onSalvar() {
      if (this.temErros) return;

      if (!this.dirty) {
        this.$vueOnToast.pop(
          "info",
          "Nenhuma alteração pendente na Matriz RFM."
        );
        return;
      }

      const options = {
        title: "Confirmar alteração de parâmetros",
        html:
          "<p>As alterações realizadas nos parâmetros da Matriz RFM poderão modificar a classificação dos clientes no próximo processamento. Deseja continuar?</p>" +
          "<p><strong>Impacto do recálculo:</strong> o motor LifeGrowth reavaliará os clientes conforme as novas faixas na próxima rotina agendada.</p>"
      };
      this.confirmSwal(options, () => this.salvar());
    },
    salvar() {
      this.salvando = true;
      this.$store
        .dispatch(RFM_SALVAR_CONFIG, configParaPayload(this.draft))
        .then(config => {
          this.metadados = config || this.metadados;
          this.configSalva = normalizarConfig(config || this.draft);
          this.draft = { ...this.configSalva };
          this.configPersistida = true;
          this.salvando = false;
          this.$vueOnToast.pop(
            "success",
            "Configuração da Matriz RFM salva com sucesso."
          );
        })
        .catch(() => {
          this.salvando = false;
        });
    }
  }
};
</script>

<style scoped>
.matriz-rfm-page {
  padding-bottom: 16px;
}

.nota-informativa {
  background: #f5f7fa;
  border-left: 3px solid var(--primary-color);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 20px;
  color: #6a6c6f;
  text-align: left;
}

.nota-informativa .v-icon {
  margin-right: 8px;
}

.rfm-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.rfm-barra-acoes {
  position: sticky;
  bottom: 8px;
  z-index: 5;
}

.rfm-barra-conteudo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  text-align: left;
}

.rfm-status-alteracoes {
  display: flex;
  align-items: center;
  gap: 8px;
}

.texto-erro {
  color: var(--danger-color);
  font-weight: 600;
}

.texto-alerta {
  color: var(--alert-color);
}

.rfm-barra-botoes {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
